import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

// Use relative URL if BACKEND_URL is empty (for production)
const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Auth error:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setToken(access_token);
      setUser(userData);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login fehlgeschlagen' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  // Rollenbasierte Zugriffsprüfung
  const hasAccess = (requiredRoles) => {
    if (!user) return false;
    if (!requiredRoles || requiredRoles.length === 0) return true;
    return requiredRoles.includes(user.role);
  };

  // Spezifische Berechtigungen
  const canViewFinances = () => ['gf', 'verwaltung'].includes(user?.role);
  const canViewVertrieb = () => ['gf', 'verwaltung', 'vertrieb'].includes(user?.role);
  const canViewKlassenbuch = () => ['gf', 'verwaltung', 'dozent'].includes(user?.role);
  const canViewTeilnehmer = () => ['gf', 'verwaltung', 'dozent'].includes(user?.role);
  const canViewAll = () => ['gf', 'verwaltung'].includes(user?.role);
  
  // NEU: Nur Markus Dickie kann Provisionen verwalten
  const canManageProvisionen = () => user?.id === 'u-linda-007' || user?.email === 'linda@dickies-helden.de';

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      logout,
      hasAccess,
      canViewFinances,
      canViewVertrieb,
      canViewKlassenbuch,
      canViewTeilnehmer,
      canViewAll,
      canManageProvisionen,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
